import { TFunction } from 'i18next';
import { cloneDeep } from 'lodash';

import type { IUser } from './interfaces';
import type { IGroup } from '../teams/interfaces';

export const ROLE_LABEL_KEYS = {
    'Chat Agent': 'agent',
    Clerk: 'member',
    'Customer Power User': 'manager',
    'Customer IT': 'it',
    'Org Admin': 'admin',
};

export const getRoleLabel = (t: TFunction, groupName: string) =>
    ROLE_LABEL_KEYS[groupName] ? t(`roles.${ROLE_LABEL_KEYS[groupName]}`) : groupName;

const ROLE_ORDER = Object.keys(ROLE_LABEL_KEYS);

export const sortRoles = <T extends { name: string }>(roles: T[]) => {
    const roleIndex = (role: T) => {
        const index = ROLE_ORDER.indexOf(role.name);
        return index === -1 ? ROLE_ORDER.length : index;
    };

    return [...roles].sort((a, b) => roleIndex(a) - roleIndex(b));
};

// values as the backend sends them in `viewer.permissions` (users/permissions.py)
const PERMISSION = {
    CHAT_AGENTS: 'chat_agents',
    DOCUMENTS: 'documents',
    PERSONAL_SMART_INBOX: 'personal_smart_inbox',
    CHANNELS: 'channels',
    ANALYTICS: 'analytics',
    USERS: 'users',
    SMART_INBOX: 'smart_inbox',
    AGENTS: 'agents',
    SELF_SERVICE_FORMS: 'self_service_forms',
    MASTER_DATA_HUB: 'master_data_hub',
    INTEGRATIONS: 'integrations',
    ACCESS_CONTROL: 'access_control',
    CUSTOMER: 'customer',
    WORKIST_ONLY: 'workist_only',
} as const;

type Permission = (typeof PERMISSION)[keyof typeof PERMISSION];

// rows of the role matrix, split like the use/manage sections of the backend enum
export const PERMISSION_GROUPS = [
    {
        key: 'use',
        permissions: [
            PERMISSION.CHAT_AGENTS,
            PERMISSION.DOCUMENTS,
            PERMISSION.PERSONAL_SMART_INBOX,
            PERMISSION.ANALYTICS,
        ],
    },
    {
        key: 'manage',
        permissions: [
            PERMISSION.CHANNELS,
            PERMISSION.USERS,
            PERMISSION.SMART_INBOX,
            PERMISSION.AGENTS,
            PERMISSION.SELF_SERVICE_FORMS,
            PERMISSION.MASTER_DATA_HUB,
            PERMISSION.INTEGRATIONS,
            PERMISSION.ACCESS_CONTROL,
            PERMISSION.CUSTOMER,
        ],
    },
];

const hasPermission = (user: IUser, permission: Permission) => Boolean(user?.permissions?.includes(permission));

export const hasWorkistPermissions = (user: IUser) => hasPermission(user, PERMISSION.WORKIST_ONLY);

export const canManageCustomer = (user: IUser) => hasPermission(user, PERMISSION.CUSTOMER);

export const canManageAccessControl = (user: IUser) => hasPermission(user, PERMISSION.ACCESS_CONTROL);

export const canManageIntegrations = (user: IUser) => hasPermission(user, PERMISSION.INTEGRATIONS);

// synced knowledge sources (SharePoint, SFTP) on a project point at company systems: the people who configure
// agents or integrations set them up (mirrors the backend's KNOWLEDGE_SOURCE_PERMISSION)
export const canConfigureKnowledgeSources = (user: IUser) =>
    hasPermission(user, PERMISSION.AGENTS) || hasPermission(user, PERMISSION.INTEGRATIONS);

export const canManageUsers = (user: IUser) => hasPermission(user, PERMISSION.USERS);

export const canGrantGroup = (viewer: IUser, groupName: string) => {
    return (viewer?.grantableGroups || []).includes(groupName);
};

// mirrors the backend no-editing-upward rule: a user may only be modified/deleted when all
// their public roles are grantable by the viewer (non-public groups like Support don't count);
// like in the backend, editing yourself is always allowed
export const canModifyUser = (viewer: IUser, user: IUser, publicRoleNames: string[]) => {
    if (viewer?.id === user?.id) {
        return true;
    }

    return (user?.groups || [])
        .filter((group: IGroup) => publicRoleNames.includes(group.name))
        .every((group: IGroup) => canGrantGroup(viewer, group.name));
};

export const canManageTeams = (user: IUser) => hasPermission(user, PERMISSION.USERS);

export const canManageCrmGroups = (user: IUser) => hasWorkistPermissions(user);

export const canManageChannels = (user: IUser) => hasPermission(user, PERMISSION.CHANNELS);

export const canManageSmartInbox = (user: IUser) => hasPermission(user, PERMISSION.SMART_INBOX);

export const canUsePersonalSmartInbox = (user: IUser) => hasPermission(user, PERMISSION.PERSONAL_SMART_INBOX);

export const canSeeDocuments = (user: IUser) => hasPermission(user, PERMISSION.DOCUMENTS);

export const canManageMasterData = (user: IUser) => hasPermission(user, PERMISSION.MASTER_DATA_HUB);

export const canManageSelfServiceForms = (user: IUser) => hasPermission(user, PERMISSION.SELF_SERVICE_FORMS);

export const canSeeSupportUserConfigs = (user: IUser) => hasWorkistPermissions(user);

export const canSkipMfa = (user: IUser) => hasWorkistPermissions(user);

export const canManageAllDocumentTypes = (user: IUser) => hasWorkistPermissions(user);

export const canSeeTestingDocuments = (user: IUser) => hasWorkistPermissions(user);

export const canReadAllCustomerChats = (user: IUser) => hasWorkistPermissions(user);

export const canSeeDeletedDocuments = (user: IUser) => hasWorkistPermissions(user);

export const canSeeAnalytics = (user: IUser) => hasPermission(user, PERMISSION.ANALYTICS);

export const canDownloadAnalytics = (user: IUser) => hasPermission(user, PERMISSION.ANALYTICS);

// the backend still derives the isAgentsEnabled flags from the viewer's own permissions (TODO there),
// so the entitlement check only becomes meaningful once they are customer-scoped
export const canSeeAgents = (user: IUser) =>
    Boolean(user?.customer?.isAgentsEnabled) &&
    (hasPermission(user, PERMISSION.AGENTS) || hasPermission(user, PERMISSION.CHAT_AGENTS));

export const canManageAgents = (user: IUser) =>
    Boolean(user?.customer?.isAgentsEnabled) && hasPermission(user, PERMISSION.AGENTS);

export const canUseChat = (user: IUser) => {
    return Boolean(user?.isChatEnabled);
};

export const canSeeVoiceAgents = (user: IUser) =>
    Boolean(user?.customer?.isVoiceAgentsEnabled) && hasPermission(user, PERMISSION.AGENTS);

export const canManageVoiceAgents = (user: IUser) => canSeeVoiceAgents(user);

export const canSeeAssistants = (user: IUser) => canSeeAgents(user) || canSeeVoiceAgents(user);

export const canSeeMailboxes = (user: IUser) => canManageSmartInbox(user) || canUsePersonalSmartInbox(user);

// the API returns the whole company's personal mailboxes; others' were only ever shown on the Users page
export const canSeeOthersPersonalMailboxes = (user: IUser) => canManageUsers(user);

export const isOwnMailbox = (user: IUser, mailbox: { createdBy?: { id?: string | number } | null }) =>
    String(mailbox.createdBy?.id ?? '') === String(user?.id ?? '');

export const canUseSupportMode = (user: IUser) => hasWorkistPermissions(user);

export const canOpenIntegrationsInAdmin = (user: IUser) => hasWorkistPermissions(user);

export const canCreateUser = (user: IUser) => hasWorkistPermissions(user);

// expert knobs are hidden from customers on purpose; the backend accepts them with CHANNELS
export const canConfigureLlmAdvancedSettings = (user: IUser) => hasWorkistPermissions(user);

export const isChatAgentOnlyUser = (user: IUser) => {
    return hasPermission(user, PERMISSION.CHAT_AGENTS) && !hasPermission(user, PERMISSION.DOCUMENTS);
};

/* webauthn */

const ab2str = (buf) => {
    if (buf == null) return null;
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buf)));
};

const b64str2ab = (b64_encoded_string) => {
    if (b64_encoded_string == null) return null;

    const str = atob(b64_encoded_string.replace(/_/g, '/').replace(/-/g, '+'));
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);

    for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
    }

    return buf;
};

export const isWebauthnSupported = 'credentials' in navigator && 'create' in navigator.credentials;

export const createPasskeyCredentials = async (options: any) => {
    options = cloneDeep(options);

    options.challenge = b64str2ab(options.challenge);
    options.user.id = b64str2ab(options.user.id);

    for (let i = 0; i < options.excludeCredentials.length; i++) {
        options.excludeCredentials[i].id = b64str2ab(options.excludeCredentials[i].id);
    }

    const attestationCredential: any = await navigator.credentials.create({ publicKey: options });

    let response = attestationCredential.response;
    return {
        id: attestationCredential.id,
        rawId: ab2str(attestationCredential.rawId),
        response: {
            clientDataJSON: ab2str(response.clientDataJSON),
            attestationObject: ab2str(response.attestationObject),
        },
        type: attestationCredential.type,
    };
};

export const getPasskeyCredentials = async (options: any) => {
    options = cloneDeep(options);

    options.challenge = b64str2ab(options.challenge);

    for (let i = 0; i < options.allowCredentials.length; i++) {
        options.allowCredentials[i].id = b64str2ab(options.allowCredentials[i].id);
    }

    const assertionCredential: any = await navigator.credentials.get({ publicKey: options });

    let response = assertionCredential.response;
    return {
        id: assertionCredential.id,
        rawId: ab2str(assertionCredential.rawId),
        response: {
            clientDataJSON: ab2str(response.clientDataJSON),
            authenticatorData: ab2str(response.authenticatorData),
            signature: ab2str(response.signature),
            userHandle: ab2str(response.userHandle),
        },
        type: assertionCredential.type,
    };
};
